/**
 * Pixels module — installs when the config (or `init({ pixels })`) carries at
 * least one pixel and pixels are not disabled. Each platform handler
 * subscribes to the PAGE_VIEW / ECOM_CLICKOUT / SESSION_BID_CENT_INCREMENT
 * bus signals.
 */

import type { Pixel, PixelPlatform } from '../../core/config/types';
import { track } from '../../core/analytics/track';
import { on, SdkEvent, type Unsubscribe } from '../../core/events/bus';
import { debug, warn } from '../../util/log';
import type { SdkModuleDefinition, SdkContext } from '../types';
import { _resetBaiduForTests, registerBaidu } from './baidu';
import { _resetFacebookForTests, registerFacebook } from './facebook';
import { _resetGoogleAdsForTests, registerGoogleAds } from './googleAds';
import { _resetOutbrainForTests, registerOutbrain } from './outbrain';
import { _resetPinterestForTests, registerPinterest } from './pinterest';
import { _resetQuoraForTests, registerQuora } from './quora';
import { _resetTaboolaForTests, registerTaboola } from './taboola';
import type { PixelContext, PixelRegister, PixelRegisterMap } from './types';

const registers = {
  facebook: registerFacebook,
  google: registerGoogleAds,
  pinterest: registerPinterest,
  quora: registerQuora,
  taboola: registerTaboola,
  outbrain: registerOutbrain,
  baidu: registerBaidu,
} as const satisfies PixelRegisterMap;

export interface PixelsModuleApi {
  /** The pixels wired on this page (config-derived + manual, deduped). */
  list(): Pixel[];
}

export function wirePixels(pixels: Pixel[], context: PixelContext): void {
  debug(
    'wiring pixels',
    pixels.map((pixel) => ({ platform: pixel.platform, pixelId: pixel.pixel_id })),
  );
  for (const pixel of pixels) {
    const register = lookupRegister(pixel.platform);
    if (!register) {
      warn(`no register for pixel platform: ${pixel.platform}`);
      continue;
    }
    try {
      // Cast inside the boundary — `register` was looked up by the same discriminant.
      (register as PixelRegister<Pixel>)(pixel, context);
      debug(`pixel registered: ${pixel.platform}`, { pixelId: pixel.pixel_id });
    } catch (err) {
      warn(`pixel ${pixel.platform} register threw`, err);
    }
  }
}

function lookupRegister<K extends PixelPlatform>(
  platform: K,
): (typeof registers)[K] | undefined {
  return registers[platform];
}

let centValueUnsub: Unsubscribe | null = null;

export const pixelsModule: SdkModuleDefinition<PixelsModuleApi> = {
  id: 'pixels',
  isEnabled(config, options) {
    return !options.disablePixels && config.pixels.length > 0;
  },
  install(ctx: SdkContext): PixelsModuleApi {
    const pixels = ctx.getConfig().pixels;
    wirePixels(pixels, {
      getIdentity: () => ctx.getIdentity(),
      getConsent: () => ctx.getConsent(),
      getPageParams: () => ctx.getPageParams(),
      getConfig: () => ctx.getConfig(),
      getOptions: () => ctx.getOptions(),
    });

    // BI mirror of milestone reporting — once per milestone, platform-independent
    // (yoto fires it from the active traffic-source pixel; consumer FACEBOOK_CENT_VALUE).
    centValueUnsub?.();
    centValueUnsub = on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
      track('fb_event:cent_value', { v: cents });
    });

    return {
      list: () => [...pixels],
    };
  },
};

/** Test-only — clears all per-pixel module flags so re-registration is clean. */
export function _resetPixelsForTests(): void {
  centValueUnsub?.();
  centValueUnsub = null;
  _resetBaiduForTests();
  _resetFacebookForTests();
  _resetGoogleAdsForTests();
  _resetOutbrainForTests();
  _resetPinterestForTests();
  _resetQuoraForTests();
  _resetTaboolaForTests();
}
